import L from 'leaflet';
import markerIcon from 'bundle-text:../img/icons/marker.svg';
import instagramIcon from 'bundle-text:../img/icons/instagram.svg';
import homeIcon from 'bundle-text:../img/icons/home.svg';
import { MarkerData, partners, legendEntries, TagInfo } from './data';

type RenderedPartner = MarkerData & {
  marker: null | L.Marker;
  popupContent: string;
};

// Augments partners to include binding between partner data and marker
const renderedPartners: RenderedPartner[] = partners.map((data) => ({
  ...data,
  marker: null,
  popupContent: popupHtmlFactory(data),
}));

export function init() {
  const map = createMap();
  const tileLayer = createTileLayer();

  const markers: L.Marker[] = [];
  addMarkers(map, markers);

  // Add zoom
  L.control.zoom({ position: 'bottomright' }).addTo(map);

  const legend = createLegend(legendEntries);

  tileLayer.addTo(map);
  legend.addTo(map);

  addEventsToMap(map);
}

function createMap() {
  return L.map('map-container', {
    center: [48.527645067280346, 8.671302795410158],
    zoom: 5,
    minZoom: 3,
    zoomControl: false,
    scrollWheelZoom: false,
    // attributionControl: false
  }); //.setView();
}

function createTileLayer() {
  return L.tileLayer(
    'https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoiYW9zYyIsImEiOiJja3dwM2l0amIwOTN5MnVsY2x0dHFtbm9oIn0._tGZukSpQWascmszgGrfjQ',
    {
      attribution:
        'Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, Imagery © <a href="https://www.mapbox.com/">Mapbox</a>',
      id: 'aosc/ckwp3n4ry51kp14n34l20chzo/draft', //FIXME change to production depending on build
      tileSize: 512,
      zoomOffset: -1,
    }
  );
}

function createDivIcon(svg: string) {
  return L.divIcon({
    html: svg,
    iconSize: [30, 30],
    iconAnchor: [15, 10],
    className: 'map__marker-icon',
  });
}

function popupHtmlFactory(partnerData: MarkerData) {
  const infoTags = partnerData.colourSources.concat(partnerData.infoTags || []);
  return `
  <div class="popup-wrapper has-half-letter-spacing">
  <div class="mb-2"><strong class="is-size-5">${partnerData.company}</strong> ${
    partnerData.description
  }</div>
  ${
    partnerData.communityPage
      ? `<div class="mb-2">Read more about ${partnerData.company}</div>`
      : ''
  }
  ${infoTags.reduce((acc, infoTag) => {
    return (
      acc +
      `<div class="popup__line-info mb-1"><span class="icon mr-1">${infoTag.icon}</span>${infoTag.name}</div>`
    );
  }, '')}
  <div class="popup__line-info mb-1"><span class="icon mr-1">${markerIcon}</span>${
    partnerData.location
  }</div>
  
  <div class="popup__line-info mt-2">
    ${
      partnerData.tags?.reduce((acc, tag) => {
        return acc + `<span class="tag is-black mr-1">${tag}</span>`;
      }, '') || ''
    }
    ${
      partnerData.instagram
        ? `<a class="popup__link" href="${partnerData.instagram}" target="_blank">
          <span class="icon mr-3">${instagramIcon}</span>
        </a>`
        : ''
    }
    ${
      partnerData.website
        ? `<a class="popup__link" href="${partnerData.website}" target="_blank">
          <span class="icon mr-3">${homeIcon}</span>${partnerData.website}
        </a>`
        : ''
    }
  </div>
</div>
  `;
}

function addMarkers(map: L.Map, markers: L.Marker[]) {
  renderedPartners.forEach((partner) => {
    const marker = L.marker(partner.latLng, {
      icon: createDivIcon(partner.markerSvg),
    });
    marker.bindPopup(popupHtmlFactory(partner));
    marker.addTo(map);
    // @ts-ignore
    marker.getElement().dataset.colourSources = partner.colourSources
      .map((colourSource) => colourSource.filterBy || colourSource.name)
      .flat()
      .join(',');

    partner.marker = marker;

    markers.push(marker);
  });
}

function createLegend(legendEntries: TagInfo[]) {
  const legend = new L.Control({ position: 'topleft' });

  // Create legend on the left
  legend.onAdd = function (map) {
    var div = L.DomUtil.create('div', 'legend');
    div.innerHTML += `
    <div class="ml-2 mt-6 map__legend">
      <h3 class="map__legend-header has-text-weight-semibold mb-3">Colour source</h3>
      ${legendEntries.reduce((prevValue, legendEntry) => {
        return (
          prevValue +
          `<div class="map__legend-entry mb-1" data-colour-sources="${legendEntry.name}">
            <i class="icon mr-2">${legendEntry.icon}</i><span class="is-size-7 map__legend-entry__name">${legendEntry.name}</span>
          </div>`
        );
      }, '')}
    </div>`;

    return div;
  };

  return legend;
}

function addEventsToMap(map: L.Map) {
  map.on('click', function (event) {
    // @ts-ignore
    const target: HTMLElement = event.originalEvent.target;

    const isPopupOpen = map.getContainer().querySelector('.leaflet-popup');
    if (isPopupOpen) {
      // Let the popup close, without applying filters
      return;
    }

    // Click outside legend
    const legendEntry: HTMLElement | null =
      target.closest('.map__legend-entry');
    const clickedInsideLegend = Boolean(legendEntry);

    const clickedColourSourceName = legendEntry?.dataset.colourSources;

    renderedPartners.forEach((partner) => {
      const marker = partner.marker;
      if (marker === null) {
        return;
      }

      const usesClickedColourSource =
        clickedColourSourceName &&
        partnerUsesColourSourceName(partner, clickedColourSourceName);

      if (usesClickedColourSource) {
        marker.setOpacity(1);
        marker.bindPopup(partner.popupContent);
        marker.setZIndexOffset(10);
        // TODO swap icon for clickedColourSourceName icon if applicable https://css-tricks.com/swapping-svg-icons/
      } else {
        marker.setOpacity(0.3);
        marker.unbindPopup();
        marker.setZIndexOffset(-10);
        // TODO swap back for clickedColourSourceName icon if applicable https://css-tricks.com/swapping-svg-icons/
      }
      if (!clickedInsideLegend) {
        // Reset
        marker.setOpacity(1);
        marker.bindPopup(partner.popupContent);
      }
    });
  });
}

function partnerUsesColourSourceName(
  partner: RenderedPartner,
  colourSourceName: string
) {
  return partner.colourSources.some(
    (partnerColourSource) =>
      partnerColourSource.name === colourSourceName ||
      partnerColourSource.filterBy?.includes(colourSourceName)
  );
}

init();
